// Initialisation Leaflet + plugins (draw & markercluster)
// Stratégie :
//  - Import ESM de Leaflet
//  - Import du code source UMD des plugins comme texte (?raw)
//  - Exécution contrôlée dans un Function(L) pour fournir l'instance Leaflet sans dépendre d'une variable globale.
//  - Idempotence assurée.

import L from "leaflet"
import "leaflet/dist/leaflet.css"
import "leaflet-draw/dist/leaflet.draw.css"
import "leaflet.markercluster/dist/MarkerCluster.css"
import "leaflet.markercluster/dist/MarkerCluster.Default.css"

// IMPORTANT: importer le code brut des plugins
// @ts-ignore - Vite supporte ?raw pour obtenir le texte brut
import leafletDrawSource from "leaflet-draw/dist/leaflet.draw.js?raw"
// @ts-ignore
import markerClusterSource from "leaflet.markercluster/dist/leaflet.markercluster.js?raw"

let initialized = false
let initializing = false

/**
 * Vérifie si un plugin Leaflet donné est déjà chargé.
 * @param label Nom du plugin ('draw' ou 'cluster')
 * @returns true si le plugin est chargé, false sinon
 */
function isPluginLoaded(label: string): boolean {
  if (label === "draw") return !!(L as any).Control?.Draw
  if (label === "cluster") return !!(L as any).MarkerClusterGroup
  return false
}

/**
 * Exécute le code source d'un plugin Leaflet une seule fois si non déjà chargé.
 * @param src Code source du plugin (UMD sous forme de texte)
 * @param label Nom du plugin ('draw' ou 'cluster')
 */
function runPluginSourceOnce(src: string, label: string) {
  try {
    if (isPluginLoaded(label)) return
    const wrapped = new Function("L", `${src}\n;return L;`)
    wrapped(L)
    if (!isPluginLoaded(label)) {
      console.warn(`[leaflet-init] Plugin ${label} exécuté mais non disponible sur L`)
    }
  } catch (e) {
    console.error(`[leaflet-init] Erreur exécution plugin ${label}:`, e)
    throw e
  }
}

/**
 * Initialise les plugins Leaflet (draw et markercluster) de façon idempotente et asynchrone.
 * Garantit que l'initialisation n'est faite qu'une seule fois, même en cas d'appels concurrents.
 * @returns Promise résolue quand les plugins sont chargés
 */
async function initializeLeafletPlugins(): Promise<void> {
  if (initialized) return
  if (initializing) {
    await new Promise<void>((resolve) => {
      const check = (): void => {
        if (initialized) resolve()
        else setTimeout(check, 20)
      }
      check()
    })
    return
  }
  initializing = true
  debugLogger("Initialisation plugins via évaluation contrôlée...")
  try {
    runPluginSourceOnce(leafletDrawSource, "draw")
    runPluginSourceOnce(markerClusterSource, "cluster")
    initialized = true
    debugLogger("Plugins chargés", {
      drawOk: isPluginLoaded("draw"),
      clusterOk: isPluginLoaded("cluster"),
    })
  } catch (err) {
    console.error("[leaflet-init] Échec initialisation plugins:", err)
    throw err
  } finally {
    initializing = false
  }
}

function debugLogger(message: string, ...optionalParams: any[]): void {
  if (process.env.NODE_ENV === "development") {
    console.log(`[leaflet-init] ${message}`, ...optionalParams)
  }
}

export { initializeLeafletPlugins }
